import type {
  ChecklistCategory,
  ChecklistItem,
  Priority,
  ShoppingItem,
  Stage,
} from '../types';

export type CustomChecklistEntry = {
  stageId: string;
  id: string;
  text: string;
  category: ChecklistCategory;
  note?: string;
};

export type CustomShoppingEntry = {
  stageId: string;
  id: string;
  name: string;
  priority: Priority;
  timing: string;
  priceYenMin: number;
  priceYenMax: number;
  note?: string;
};

/** ユーザーごとのチェックリスト／買い物カスタム */
export type ItemCustomizations = {
  /** 非表示にした標準チェック項目 `stageId:itemId` */
  hiddenChecklist: string[];
  /** 非表示にした標準買い物 `stageId:itemId` */
  hiddenShopping: string[];
  /** 標準チェック項目の文言上書き */
  checklistText: Record<string, string>;
  /** 標準買い物の名前上書き */
  shoppingName: Record<string, string>;
  customChecklist: CustomChecklistEntry[];
  customShopping: CustomShoppingEntry[];
};

export function emptyCustomizations(
  partial?: Partial<ItemCustomizations>
): ItemCustomizations {
  return {
    hiddenChecklist: Array.isArray(partial?.hiddenChecklist)
      ? partial!.hiddenChecklist.filter((k) => typeof k === 'string')
      : [],
    hiddenShopping: Array.isArray(partial?.hiddenShopping)
      ? partial!.hiddenShopping.filter((k) => typeof k === 'string')
      : [],
    checklistText:
      partial?.checklistText && typeof partial.checklistText === 'object'
        ? Object.fromEntries(
            Object.entries(partial.checklistText).filter(
              ([, v]) => typeof v === 'string' && v.trim()
            )
          )
        : {},
    shoppingName:
      partial?.shoppingName && typeof partial.shoppingName === 'object'
        ? Object.fromEntries(
            Object.entries(partial.shoppingName).filter(
              ([, v]) => typeof v === 'string' && v.trim()
            )
          )
        : {},
    customChecklist: Array.isArray(partial?.customChecklist)
      ? partial!.customChecklist
          .filter((c) => c && typeof c.stageId === 'string' && typeof c.text === 'string')
          .map((c) => ({
            stageId: c.stageId,
            id: typeof c.id === 'string' ? c.id : createCustomId('c'),
            text: c.text.trim(),
            category: (c.category as ChecklistCategory) || 'life',
            note: typeof c.note === 'string' ? c.note : undefined,
          }))
      : [],
    customShopping: Array.isArray(partial?.customShopping)
      ? partial!.customShopping
          .filter((c) => c && typeof c.stageId === 'string' && typeof c.name === 'string')
          .map((c) => ({
            stageId: c.stageId,
            id: typeof c.id === 'string' ? c.id : createCustomId('s'),
            name: c.name.trim(),
            priority: (c.priority as Priority) || 'should',
            timing: typeof c.timing === 'string' && c.timing.trim() ? c.timing : 'いつでも',
            priceYenMin: typeof c.priceYenMin === 'number' ? c.priceYenMin : 0,
            priceYenMax: typeof c.priceYenMax === 'number' ? c.priceYenMax : 0,
            note: typeof c.note === 'string' ? c.note : undefined,
          }))
      : [],
  };
}

export function createCustomId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}

export function itemKey(stageId: string, itemId: string): string {
  return `${stageId}:${itemId}`;
}

/** 標準ステージに、非表示・上書き・追加を反映する */
export function applyCustomizationsToStages(
  stages: Stage[],
  custom: ItemCustomizations | null | undefined
): Stage[] {
  const c = emptyCustomizations(custom ?? undefined);
  const hiddenCheck = new Set(c.hiddenChecklist);
  const hiddenShop = new Set(c.hiddenShopping);

  return stages.map((stage) => {
    const checklist: ChecklistItem[] = stage.checklist
      .filter((item) => !hiddenCheck.has(itemKey(stage.id, item.id)))
      .map((item) => {
        const key = itemKey(stage.id, item.id);
        const text = c.checklistText[key];
        return text ? { ...item, text } : item;
      });

    for (const entry of c.customChecklist) {
      if (entry.stageId !== stage.id) continue;
      checklist.push({
        id: entry.id,
        text: entry.text,
        category: entry.category,
        note: entry.note,
        custom: true,
      });
    }

    const shopping: ShoppingItem[] = stage.shopping
      .filter((item) => !hiddenShop.has(itemKey(stage.id, item.id)))
      .map((item) => {
        const key = itemKey(stage.id, item.id);
        const name = c.shoppingName[key];
        return name ? { ...item, name } : item;
      });

    for (const entry of c.customShopping) {
      if (entry.stageId !== stage.id) continue;
      shopping.push({
        id: entry.id,
        name: entry.name,
        priority: entry.priority,
        timing: entry.timing,
        priceYenMin: entry.priceYenMin,
        priceYenMax: entry.priceYenMax,
        note: entry.note,
        custom: true,
      });
    }

    return { ...stage, checklist, shopping };
  });
}

/** 家族同期用: カスタムをマージ（追加はIDで統合、非表示は和集合、上書きは新しい方優先） */
export function mergeCustomizations(
  local: ItemCustomizations | null | undefined,
  remote: ItemCustomizations | null | undefined
): ItemCustomizations {
  const a = emptyCustomizations(local ?? undefined);
  const b = emptyCustomizations(remote ?? undefined);

  const checklistMap = new Map<string, CustomChecklistEntry>();
  for (const item of [...a.customChecklist, ...b.customChecklist]) {
    checklistMap.set(`${item.stageId}:${item.id}`, item);
  }
  const shoppingMap = new Map<string, CustomShoppingEntry>();
  for (const item of [...a.customShopping, ...b.customShopping]) {
    shoppingMap.set(`${item.stageId}:${item.id}`, item);
  }

  return emptyCustomizations({
    hiddenChecklist: Array.from(new Set([...a.hiddenChecklist, ...b.hiddenChecklist])),
    hiddenShopping: Array.from(new Set([...a.hiddenShopping, ...b.hiddenShopping])),
    checklistText: { ...a.checklistText, ...b.checklistText },
    shoppingName: { ...a.shoppingName, ...b.shoppingName },
    customChecklist: Array.from(checklistMap.values()),
    customShopping: Array.from(shoppingMap.values()),
  });
}

export function customizationsSignature(custom: ItemCustomizations | null | undefined): string {
  try {
    return JSON.stringify(emptyCustomizations(custom ?? undefined));
  } catch {
    return '';
  }
}
